import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeader } from './Services';
import { listGallery, GALLERY_COLOR_BG, SPAN_CLASS, type GalleryItem } from '../lib/gallery';

export default function Gallery() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    listGallery().then(setItems).catch(() => {});
  }, []);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
      if (e.key === 'ArrowRight') setActive((i) => (i === null ? null : (i + 1) % items.length));
      if (e.key === 'ArrowLeft') setActive((i) => (i === null ? null : (i - 1 + items.length) % items.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, items.length]);

  if (items.length === 0) return null;

  return (
    <section id="galleri" className="relative py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Galleri"
          title={<>Stunder från <span className="text-sky">verkliga fester</span>.</>}
          description="Ett axplock från kalas, festivaler, skolavslutningar och företagsdagar — överallt där vi har varit med och spridit glädje."
        />

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[200px] gap-3 sm:gap-4">
          {items.map((t, i) => (
            <motion.button
              key={t.id}
              type="button"
              onClick={() => setActive(i)}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
              className={`group relative overflow-hidden rounded-3xl ${GALLERY_COLOR_BG[t.color] ?? 'bg-ink'} ${t.span ? SPAN_CLASS[t.span] : ''} shadow-pop`}
              aria-label={`Öppna ${t.label}`}
            >
              {t.image ? (
                <img
                  src={t.image}
                  alt={t.label}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 stripes text-cream/15" />
              )}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-ink/70 to-transparent">
                <div className="text-cream text-sm font-semibold translate-y-1 opacity-90 group-hover:translate-y-0 group-hover:opacity-100 transition">
                  {t.label}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-md grid place-items-center p-4"
          >
            <motion.div
              key={active}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-w-3xl aspect-[4/3] rounded-[2rem] ${GALLERY_COLOR_BG[items[active].color] ?? 'bg-ink'} shadow-bouncy overflow-hidden`}
            >
              {items[active].image ? (
                <img
                  src={items[active].image}
                  alt={items[active].label}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 stripes text-cream/15" />
              )}
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-ink/80 to-transparent text-cream">
                <div className="text-xs uppercase tracking-wider opacity-70">Galleri</div>
                <div className="font-display text-2xl font-semibold">{items[active].label}</div>
              </div>
              <button
                type="button"
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 size-11 grid place-items-center rounded-full bg-cream text-ink shadow-pop"
                aria-label="Stäng"
              >
                ✕
              </button>
              {items.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setActive((i) => (i === null ? null : (i - 1 + items.length) % items.length)); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 size-11 grid place-items-center rounded-full bg-cream/20 hover:bg-cream/40 text-cream transition-colors"
                    aria-label="Föregående"
                  >←</button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setActive((i) => (i === null ? null : (i + 1) % items.length)); }}
                    className="absolute right-16 top-1/2 -translate-y-1/2 size-11 grid place-items-center rounded-full bg-cream/20 hover:bg-cream/40 text-cream transition-colors"
                    aria-label="Nästa"
                  >→</button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
